import { CreditCardOutlined } from '@ant-design/icons';
import { Payment } from '@interfaces/payment';
import { ISmsCreditPackage } from '@interfaces/sms-credits';
import { useNavigation } from '@refinedev/core';
import { convertPrice } from '@utils/resource';
import { Alert, Button, Descriptions, Modal } from 'antd';
import { useTranslation } from 'react-i18next';

const KNOWN_ERRORS = ['payment_failed', 'no_default_card', 'package_not_found'];

type Props = {
    open: boolean;
    pkg: ISmsCreditPackage;
    defaultCard: Payment.IPaymentMethod | null;
    isPaying: boolean;
    errorCode: string | null;
    onPay: () => void;
    onClose: () => void;
};

/** Order summary before the charge: what, how much, which card. Cancel stays available until the request is in flight. */
export const SmsCreditPurchaseConfirmModal: React.FC<Props> = ({
    open,
    pkg,
    defaultCard,
    isPaying,
    errorCode,
    onPay,
    onClose,
}) => {
    const { t, i18n } = useTranslation(['marketplace']);
    const { push } = useNavigation();
    const price = convertPrice(pkg.price);

    return (
        <Modal
            open={open}
            onCancel={isPaying ? undefined : onClose}
            closable={!isPaying}
            maskClosable={false}
            title={t('smsCredits.purchase.confirmTitle')}
            footer={[
                <Button key="cancel" onClick={onClose} disabled={isPaying}>
                    {t('smsCredits.purchase.cancel')}
                </Button>,
                defaultCard ? (
                    <Button key="ok" type="primary" loading={isPaying} onClick={onPay}>
                        {t('smsCredits.purchase.confirm', { price })}
                    </Button>
                ) : (
                    <Button key="billing" type="primary" onClick={() => push('/setting/billing')}>
                        {t('smsCredits.purchase.addCard')}
                    </Button>
                ),
            ]}
        >
            <Descriptions column={1} size="small" bordered className="sms-credit-order-summary">
                <Descriptions.Item label={t('smsCredits.purchase.package')}>
                    {t('smsCredits.package.credits', {
                        credits: pkg.credits.toLocaleString(i18n.language),
                    })}
                </Descriptions.Item>
                <Descriptions.Item label={t('smsCredits.purchase.cardLabel')}>
                    {defaultCard ? (
                        <span>
                            <CreditCardOutlined aria-hidden="true" style={{ marginRight: 8 }} />
                            {t('smsCredits.purchase.cardEnding', {
                                brand: defaultCard.brand ?? '',
                                last4: defaultCard.last4,
                            })}
                        </span>
                    ) : (
                        '—'
                    )}
                </Descriptions.Item>
                <Descriptions.Item label={t('smsCredits.purchase.total')}>
                    <strong className="sms-credit-order-total">{price}</strong>
                </Descriptions.Item>
            </Descriptions>
            <p className="sms-credit-order-note">{t('smsCredits.purchase.rollOverNote')}</p>
            {!defaultCard && (
                <Alert type="warning" showIcon message={t('smsCredits.purchase.noCard')} />
            )}
            {errorCode && (
                <Alert
                    type="error"
                    showIcon
                    role="alert"
                    style={{ marginTop: 12 }}
                    message={t(
                        `smsCredits.errors.${
                            KNOWN_ERRORS.includes(errorCode) ? errorCode : 'unknown'
                        }`,
                    )}
                />
            )}
        </Modal>
    );
};
